/**
 * useOutreach.js — Hooks for outreach actions CRUD
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

/** Fetch all outreach actions */
export function useOutreachActions() {
  return useQuery({
    queryKey: queryKeys.outreach.all,
    queryFn: async () => {
      const data = await api.get('/outreach-actions');
      return data.map(mapFromDb);
    },
  });
}

/** Fetch outreach actions for a specific contact */
export function useOutreachByContact(contactId) {
  return useQuery({
    queryKey: queryKeys.outreach.byContact(contactId),
    queryFn: async () => {
      const data = await api.get(`/outreach-actions?contact_id=${contactId}`);
      return data.map(mapFromDb);
    },
    enabled: !!contactId,
  });
}

/** Log a new outreach action */
export function useLogOutreachAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action) => {
      const data = await api.post('/outreach-actions', mapToDb(action));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.outreach.all });
      if (data.contactId) {
        qc.invalidateQueries({ queryKey: queryKeys.outreach.byContact(data.contactId) });
      }
    },
  });
}

/** Delete an outreach action */
export function useDeleteOutreachAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.del(`/outreach-actions/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.outreach.all });
    },
  });
}
